import { noButtons } from '@byecode/ui'
import { type Field } from '@lighthouse/core'
import React, { useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'

import { useApplicationContext } from '../../contexts'
import { TableField } from './TableField'
import { TableIndex } from './TableIndex'

interface TableHeaderProps {
    columns: Field[]
    primaryField: string
    scrollRef: React.RefObject<HTMLDivElement>
    onAllRecordSelect?: (selected: boolean) => void
    paddingRight?: number
}

const SCxTableHeader = styled.div<{ backgroundColor?: string }>`
    position: sticky;
    top: 0;
    z-index: 2;
    display: flex;
    width: 100%;
    background-color: var(--color-white);
    overflow: auto hidden;
    ${noButtons('0px')}
`

export const TableHeader = React.forwardRef<HTMLDivElement, TableHeaderProps>(({ columns, primaryField, scrollRef, paddingRight }, ref) => {
    const [firstColumn, ...afterColumns] = columns
    const { isMobile } = useApplicationContext()
    const [scrollDom, setScrollDom] = useState<HTMLDivElement | null>(scrollRef?.current)

    const estimateSize = useMemo(() => {
        if (!scrollDom || !isMobile) {
            return 160
        }
        const { width } = scrollDom.getBoundingClientRect()
        const renderColumnsNum = columns.length - (isMobile ? 1 : 0)

        return width > renderColumnsNum * 160 ? width / renderColumnsNum : 160
    }, [columns.length, isMobile, scrollDom])

    useEffect(() => {
        if (!scrollDom) {
            setScrollDom(scrollRef?.current)
        }
    }, [scrollDom, scrollRef])

    return (
        <SCxTableHeader ref={ref} style={{ paddingRight }}>
            {isMobile || <TableIndex background="var(--color-gray-100)" />}
            {afterColumns.map(field => {
                return <TableField key={field.id} field={field} isPrimaryField={field.id === primaryField} width={estimateSize} />
            })}
        </SCxTableHeader>
    )
})
